"use client";

import { useState, useCallback, useEffect } from "react";
import { useAuth } from "@clerk/nextjs";
import { Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AuthCard } from "@/components/auth/AuthCard";
import { PlanSelector } from "@/components/membership/PlanSelector";
import { trackEvent } from "@/lib/analytics";

type Step = "auth" | "plan";

type MembershipPaymentModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source?: string;
};

const PENDING_KEY = "membership_payment_pending";

export function MembershipPaymentModal({
  open,
  onOpenChange,
  source = "cta",
}: MembershipPaymentModalProps) {
  const { isSignedIn, isLoaded } = useAuth();
  const [step, setStep] = useState<Step>(isSignedIn ? "plan" : "auth");
  const [plan, setPlan] = useState<"monthly" | "yearly">("yearly");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isLoaded && isSignedIn && step === "auth") {
      setStep("plan");
    }
  }, [isLoaded, isSignedIn, step]);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    try {
      const pending = sessionStorage.getItem(PENDING_KEY);
      if (pending) {
        sessionStorage.removeItem(PENDING_KEY);
        onOpenChange(true);
        setStep("plan");
      }
    } catch {}
  }, [isLoaded, isSignedIn, onOpenChange]);

  const resetState = useCallback(() => {
    setStep(isSignedIn ? "plan" : "auth");
    setPlan("yearly");
    setLoading(false);
    setError(null);
  }, [isSignedIn]);

  const handleAuthSuccess = () => {
    try { sessionStorage.setItem(PENDING_KEY, "1"); } catch {}
    setStep("plan");
  };

  const handleCheckout = async () => {
    setLoading(true);
    setError(null);
    try {
      const membershipRes = await fetch("/api/membership");
      if (!membershipRes.ok) {
        setError("Mitgliedschaftsstatus konnte nicht geprüft werden. Bitte versuche es erneut.");
        return;
      }
      const data = await membershipRes.json();

      if (data.isActive && data.membership?.status === "active") {
        const portalRes = await fetch("/api/stripe/portal", { method: "POST" });
        const portalData = await portalRes.json();
        if (portalData.url) {
          window.location.href = portalData.url;
        } else {
          setError("Verwaltungsportal konnte nicht geöffnet werden. Bitte versuche es erneut.");
        }
        return;
      }

      const params = new URL(window.location.href).searchParams;
      const utmSource = params.get("utm_source") || undefined;
      const utmCampaign = params.get("utm_campaign") || undefined;
      const utmContent = params.get("utm_content") || undefined;
      const utmMedium = params.get("utm_medium") || undefined;

      trackEvent("checkout_started", { plan, source, utm_source: utmSource, utm_campaign: utmCampaign, utm_content: utmContent, utm_medium: utmMedium });
      try { sessionStorage.setItem("checkout_context", JSON.stringify({ plan, source, source_path: window.location.pathname, utm_source: utmSource, utm_campaign: utmCampaign, utm_content: utmContent, utm_medium: utmMedium })); } catch {}

      const res = await fetch("/api/stripe/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan, source, cancelPath: window.location.pathname, source_path: window.location.pathname, utm_source: utmSource, utm_campaign: utmCampaign, utm_content: utmContent, utm_medium: utmMedium }),
      });
      const result = await res.json();
      if (result.url) {
        window.location.href = result.url;
      } else {
        setError(result.error || "Checkout konnte nicht gestartet werden. Bitte versuche es erneut.");
      }
    } catch {
      setError("Verbindungsfehler. Bitte prüfe deine Internetverbindung und versuche es erneut.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) resetState();
    onOpenChange(next);
  };

  if (!isLoaded) return null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        className={
          step === "auth"
            ? "bg-transparent border-none shadow-none max-w-lg p-0"
            : "bg-white border-none rounded-3xl max-w-md p-6 gap-6"
        }
      >
        {step === "auth" && (
          <>
            <DialogTitle className="sr-only">Anmelden</DialogTitle>
            <AuthCard onSuccess={handleAuthSuccess} skipRedirect source="membership_payment_modal" />
          </>
        )}

        {step === "plan" && (
          <>
            <DialogTitle className="text-headline-md text-black text-center">
              Plan auswählen
            </DialogTitle>

            <PlanSelector onSelect={setPlan} defaultPlan="yearly" />

            {error && (
              <p className="text-copy-sm text-red-600 text-center">{error}</p>
            )}

            <Button
              variant="sun"
              size="lg"
              className="w-full"
              onClick={handleCheckout}
              disabled={loading}
            >
              {loading ? <Loader2 className="size-4 animate-spin" /> : "Weiter zur Zahlung"}
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
